import { useEffect, useState, useCallback } from "react";
import {
  Briefcase, MapPin, Truck, Buildings, SealCheck, Warning, CurrencyDollar, Power,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { COUNTRIES, countryName } from "@/lib/countries";

const SYMBOLS = { usd: "$", eur: "€", gbp: "£", inr: "₹", cad: "C$", aud: "A$", jpy: "¥" };
const money = (n, cur) =>
  `${SYMBOLS[(cur || "usd").toLowerCase()] || `${(cur || "usd").toUpperCase()} `}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
const COVER_LABELS = {
  cargo: "Cargo / goods in transit", fleet: "Fleet & vehicle", cart: "Local cart carrier",
  business: "Business & equipment", liability: "Public & authority liability",
};

export default function AgentDesk() {
  const { user } = useAuth();
  const [view, setView] = useState("work");
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [book, setBook] = useState({ policies: [], claims: [] });
  const [offers, setOffers] = useState([]);
  const [offerFor, setOfferFor] = useState(null);
  const [oForm, setOForm] = useState({ plan_id: "", insured_value: 0, premium: "", excess: 0, term_days: 30, note: "" });
  const [respondTo, setRespondTo] = useState(null);
  const [rForm, setRForm] = useState({ premium: 0, currency: "usd", excess: 0, term_days: 30, note: "" });
  const [areaOpen, setAreaOpen] = useState(false);
  const [area, setArea] = useState({ city: "", country: "US", covers_whole_country: true, international: false });

  const load = useCallback(async () => {
    try {
      const [pf, sm, en, sh, fa, bk, of] = await Promise.all([
        api.get("/insurance/agent/profile"),
        api.get("/insurance/agent/summary"),
        api.get("/insurance/agent/enquiries"),
        api.get("/insurance/agent/shipments"),
        api.get("/insurance/agent/facilities"),
        api.get("/insurance/agent/book"),
        api.get("/insurance/offers"),
      ]);
      setProfile(pf.data);
      setSummary(sm.data);
      setEnquiries(en.data);
      setShipments(sh.data);
      setFacilities(fa.data);
      setBook(bk.data);
      setOffers(of.data);
      if (pf.data.provider) {
        setArea({
          city: pf.data.provider.city || "",
          country: (pf.data.provider.countries || [])[0] || "US",
          covers_whole_country: pf.data.provider.covers_whole_country !== false,
          international: !!pf.data.provider.international,
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const myPlans = (profile?.plans || []).filter((p) => p.active);

  const openOffer = (subject, kind) => {
    setOfferFor({ ...subject, kind });
    setOForm({
      plan_id: "",
      insured_value: Number(subject.goods_value || subject.value || 0) || 10000,
      premium: "", excess: 0, term_days: 30, note: "",
    });
  };

  const sendOffer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insurance/offers", {
        shipment_id: offerFor.kind === "shipment" ? offerFor.id : null,
        facility_id: offerFor.kind === "facility" && offerFor.source === "facility" ? offerFor.id : null,
        depot_id: offerFor.kind === "facility" && offerFor.source === "depot" ? offerFor.id : null,
        plan_id: oForm.plan_id || null,
        cover_type: offerFor.cover_type || "business",
        insured_value: Number(oForm.insured_value),
        premium: oForm.plan_id ? null : Number(oForm.premium),
        excess: Number(oForm.excess),
        term_days: Number(oForm.term_days),
        note: oForm.note || null,
      });
      toast.success("Cover offered — the customer is notified");
      setOfferFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/insurance/quote-requests/${respondTo.id}/respond`, {
        premium: Number(rForm.premium), currency: rForm.currency,
        excess: Number(rForm.excess), term_days: Number(rForm.term_days),
        note: rForm.note || null,
      });
      toast.success("Quote sent");
      setRespondTo(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveArea = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/insurance/agent/area", {
        city: area.city || null,
        countries: area.country ? [area.country] : [],
        covers_whole_country: area.covers_whole_country,
        international: area.international,
      });
      toast.success("Patch updated");
      setAreaOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const act = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (profile && !profile.provider) {
    return (
      <div data-testid="agent-desk-page">
        <PageHeader overline="Agency" title="Agent desk" description="Set your agency up to start writing cover." testid="agent-desk-header" />
        <EmptyState
          testid="no-agency"
          title="No agency profile yet"
          description="Open Insurance → Providers and register your agency. Once an admin verifies you, every enquiry, shipment and facility in your patch appears here."
        />
      </div>
    );
  }

  const VIEWS = [
    ["work", `Work in my patch (${enquiries.length + shipments.length + facilities.length})`],
    ["offers", `My offers (${offers.length})`],
    ["book", `My book (${book.policies.length})`],
    ["claims", `Claims (${book.claims.length})`],
    ["plans", `Plans (${profile?.plans?.length || 0})`],
  ];

  return (
    <div data-testid="agent-desk-page">
      <PageHeader
        overline="Agency"
        title="Agent desk"
        description="Every risk in your patch: cover enquiries, live shipping jobs and local cart runs, and the facilities on your doorstep. Offer cover, assess claims and keep your plans current."
        testid="agent-desk-header"
        action={
          <Button data-testid="edit-area-button" variant="outline" onClick={() => setAreaOpen(true)} className="w-full sm:w-auto">
            <MapPin size={16} className="mr-1.5" /> My patch
          </Button>
        }
      />

      {profile?.provider && (
        <div className="mb-5 flex flex-wrap items-center gap-2 border border-border bg-white p-3 text-xs text-slate-600" data-testid="agent-identity">
          <span className="text-sm font-semibold text-[#0F172A]">{profile.provider.name}</span>
          {profile.verified
            ? <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-[#002FA7]" data-testid="agent-verified"><SealCheck size={13} /> Verified</span>
            : <StatusBadge value="pending" testid="agent-unverified" />}
          <span data-testid="agent-area">
            <MapPin size={12} className="mr-1 inline" />
            {profile.provider.city ? `${profile.provider.city}, ` : ""}
            {(profile.provider.countries || []).map(countryName).join(", ") || "Every country"}
            {profile.provider.covers_whole_country === false ? " (city only)" : " (whole country)"}
            {profile.provider.international ? " + international" : ""}
          </span>
          {!profile.verified && (
            <span className="text-[#B3241B]" data-testid="verify-warning">
              An admin must verify you before you can quote or offer cover
            </span>
          )}
        </div>
      )}

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard testid="kpi-open-enquiries" label="Open enquiries" value={summary.open_enquiries} tone="primary" icon={Briefcase} />
          <StatCard testid="kpi-policies-written" label="Policies written" value={summary.policies_written} icon={SealCheck} />
          <StatCard testid="kpi-premium-earned" label="Premium earned" value={money(summary.premium_earned)} tone="success" icon={CurrencyDollar} />
          <StatCard testid="kpi-exposure" label="Exposure on cover" value={money(summary.exposure)} />
          <StatCard testid="kpi-claims-to-assess" label="Claims to assess" value={summary.claims_to_assess} tone={summary.claims_to_assess > 0 ? "danger" : "default"} icon={Warning} />
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="mb-5">
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="agent-views">
          {VIEWS.map(([value, label]) => (
            <TabsTrigger key={value} value={value} data-testid={`agent-view-${value}`} className="whitespace-nowrap">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* ---------------- Work in my patch ---------------- */}
      {view === "work" && (
        <div className="space-y-6" data-testid="agent-work-panel">
          <section>
            <div className="overline mb-2 text-slate-500">Cover enquiries</div>
            {enquiries.length === 0 ? (
              <EmptyState testid="no-agent-enquiries" title="No open enquiries" description="Enquiries from your patch land here the moment they are sent." />
            ) : (
              <div className="space-y-2">
                {enquiries.map((q) => (
                  <div key={q.id} data-testid={`agent-enquiry-${q.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{COVER_LABELS[q.cover_type]}</div>
                      <div className="text-xs text-slate-500">
                        {money(q.insured_value, q.currency)} · {countryName(q.country) || "any country"}
                        {q.dest_country ? ` → ${countryName(q.dest_country)}` : ""} · {q.requester_name}
                      </div>
                      {q.details && <div className="mt-1 text-xs text-slate-400">{q.details}</div>}
                    </div>
                    <Button
                      size="sm"
                      data-testid={`agent-quote-${q.id}`}
                      disabled={q.already_quoted}
                      onClick={() => { setRForm({ premium: 0, currency: q.currency || "usd", excess: 0, term_days: 30, note: "" }); setRespondTo(q); }}
                    >
                      {q.already_quoted ? "Quoted" : "Quote it"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="overline mb-2 text-slate-500">Shipping & local cart work without cover</div>
            {shipments.length === 0 ? (
              <EmptyState testid="no-agent-shipments" title="No uninsured jobs in your patch" description="Freight loads and local cart runs picked up in your area show here." />
            ) : (
              <div className="space-y-2">
                {shipments.map((s) => (
                  <div key={s.id} data-testid={`agent-shipment-${s.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm font-bold text-[#002FA7]">{s.shipment_number}</span>
                        <StatusBadge value={s.status} testid={`agent-shipment-status-${s.id}`} />
                        <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">
                          {s.booking_type === "ride" ? "Local cart" : "Freight"}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600">{s.description}</div>
                      <div className="text-xs text-slate-500">
                        <Truck size={12} className="mr-1 inline" />
                        {[s.pickup_city, countryName(s.pickup_country)].filter(Boolean).join(", ")} →
                        {" "}{[s.dropoff_city, countryName(s.dropoff_country)].filter(Boolean).join(", ")}
                        {" "}· {s.packages} pkg · {s.weight_kg} kg · goods {money(s.goods_value, s.currency)}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      data-testid={`offer-cover-shipment-${s.id}`}
                      disabled={s.offer_sent}
                      onClick={() => openOffer(s, "shipment")}
                    >
                      {s.offer_sent ? "Offered" : "Offer cover"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="overline mb-2 text-slate-500">Facilities & depots without cover</div>
            {facilities.length === 0 ? (
              <EmptyState testid="no-agent-facilities" title="No uninsured facilities in your patch" description="Warehouses, depots and yards in your area appear here." />
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {facilities.map((f) => (
                  <div key={f.id} data-testid={`agent-facility-${f.id}`} className="flex items-start justify-between gap-3 border border-border bg-white p-4">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">
                        <Buildings size={13} className="mr-1 inline" />{f.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {f.facility_type} · {[f.city, countryName(f.country)].filter(Boolean).join(", ")}
                        {f.value ? ` · valued ${money(f.value, f.currency)}` : " · no value on file"}
                      </div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-400">
                        {f.source === "depot" ? "From Logistics" : "Facility register"}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      data-testid={`offer-cover-facility-${f.id}`}
                      onClick={() => openOffer({ ...f, cover_type: "business" }, "facility")}
                    >
                      Offer cover
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ---------------- My offers ---------------- */}
      {view === "offers" && (
        <div className="space-y-2" data-testid="agent-offers-panel">
          {offers.length === 0 ? (
            <EmptyState testid="no-agent-offers" title="No offers yet" description="Offer cover on a job or facility and it shows here until the customer decides." />
          ) : offers.map((o) => (
            <div key={o.id} data-testid={`agent-offer-${o.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">{o.subject_label}</span>
                  <StatusBadge value={o.status} testid={`agent-offer-status-${o.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {COVER_LABELS[o.cover_type]} · {money(o.insured_value, o.currency)} insured ·
                  {" "}{money(o.premium, o.currency)} premium · {o.term_days}d · {o.plan_name}
                </div>
                {o.note && <div className="mt-1 text-xs text-slate-400">{o.note}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- My book ---------------- */}
      {view === "book" && (
        <div className="space-y-2" data-testid="agent-book-panel">
          {book.policies.length === 0 ? (
            <EmptyState testid="no-agent-policies" title="No policies written yet" description="Accepted quotes and offers land in your book." />
          ) : book.policies.map((p) => (
            <div key={p.id} data-testid={`agent-policy-${p.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-[#002FA7]">{p.policy_number}</span>
                  <StatusBadge value={p.status} testid={`agent-policy-status-${p.id}`} />
                  <StatusBadge value={p.payment_status === "paid" ? "approved" : "pending"} testid={`agent-policy-payment-${p.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {COVER_LABELS[p.cover_type]} · {p.holder_name} · {p.plan_name}
                  {p.facility_name ? ` · ${p.facility_name}` : ""}
                </div>
                <div className="text-xs text-slate-500">
                  {money(p.insured_value, p.currency)} insured · {money(p.premium, p.currency)} premium ·
                  {" "}{p.start_date} → {p.end_date}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Claims ---------------- */}
      {view === "claims" && (
        <div className="space-y-2" data-testid="agent-claims-panel">
          {book.claims.length === 0 ? (
            <EmptyState testid="no-agent-claims" title="No claims" description="Claims on the policies you wrote appear here for assessment." />
          ) : book.claims.map((c) => (
            <div key={c.id} data-testid={`agent-claim-${c.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-[#002FA7]">{c.claim_number}</span>
                  <StatusBadge value={c.status} testid={`agent-claim-status-${c.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {c.policy_number} · {money(c.amount, c.currency)} claimed · incident {c.incident_date} · {c.claimant_name}
                </div>
                <div className="mt-1 text-xs text-slate-500">{c.description}</div>
              </div>
              <div className="flex flex-wrap gap-2 sm:shrink-0">
                {["under_review", "approved", "rejected", "paid"].map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={s === "paid" ? "default" : "outline"}
                    data-testid={`agent-claim-${s}-${c.id}`}
                    onClick={() => act(() => api.patch(`/insurance/claims/${c.id}/status`, { status: s }), `Claim ${s.replace("_", " ")}`)}
                  >
                    {s.replace("_", " ")}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Plans ---------------- */}
      {view === "plans" && (
        <div className="space-y-2" data-testid="agent-plans-panel">
          {(profile?.plans || []).length === 0 ? (
            <EmptyState testid="no-agent-plans" title="No plans published" description="Publish a plan from Insurance → Providers so buyers get an instant premium from you." />
          ) : profile.plans.map((pl) => (
            <div key={pl.id} data-testid={`agent-plan-${pl.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">{pl.name}</span>
                  <StatusBadge value={pl.active ? "active" : "cancelled"} testid={`agent-plan-status-${pl.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {COVER_LABELS[pl.cover_type]} · {pl.rate_pct}% · min {money(pl.min_premium, pl.currency)} ·
                  {" "}excess {money(pl.excess, pl.currency)} · {pl.scope} · {pl.term_days}d
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                data-testid={`toggle-plan-${pl.id}`}
                onClick={() => act(() => api.post(`/insurance/plans/${pl.id}/toggle`), pl.active ? "Plan withdrawn" : "Plan published")}
              >
                <Power size={14} className="mr-1" /> {pl.active ? "Withdraw" : "Publish"}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Offer dialog ---------------- */}
      <Dialog open={!!offerFor} onOpenChange={(v) => !v && setOfferFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle>Offer cover</DialogTitle>
            <DialogDescription>
              {offerFor ? `${offerFor.shipment_number || offerFor.name} — the customer accepts or declines and pays online.` : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendOffer} className="space-y-3">
            <div>
              <Label>Use one of my plans (optional)</Label>
              <Select value={oForm.plan_id || "NONE"} onValueChange={(v) => setOForm({ ...oForm, plan_id: v === "NONE" ? "" : v })}>
                <SelectTrigger data-testid="offer-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Price it myself</SelectItem>
                  {myPlans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} — {p.rate_pct}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Sum insured</Label>
                <Input data-testid="offer-value-input" type="number" min="1" required value={oForm.insured_value} onChange={(e) => setOForm({ ...oForm, insured_value: e.target.value })} />
              </div>
              {!oForm.plan_id && (
                <div>
                  <Label>Premium</Label>
                  <Input data-testid="offer-premium-input" type="number" step="0.01" min="0.01" required value={oForm.premium} onChange={(e) => setOForm({ ...oForm, premium: e.target.value })} />
                </div>
              )}
              <div>
                <Label>Excess</Label>
                <Input data-testid="offer-excess-input" type="number" step="0.01" min="0" value={oForm.excess} onChange={(e) => setOForm({ ...oForm, excess: e.target.value })} />
              </div>
              <div>
                <Label>Term (days)</Label>
                <Input data-testid="offer-term-input" type="number" min="1" value={oForm.term_days} onChange={(e) => setOForm({ ...oForm, term_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Note to the customer</Label>
              <Textarea data-testid="offer-note-input" rows={2} value={oForm.note} onChange={(e) => setOForm({ ...oForm, note: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-offer-button" className="w-full sm:w-auto">Send offer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Quote dialog ---------------- */}
      <Dialog open={!!respondTo} onOpenChange={(v) => !v && setRespondTo(null)}>
        <DialogContent className="sm:max-w-md" data-testid="agent-quote-dialog">
          <DialogHeader>
            <DialogTitle>Quote this enquiry</DialogTitle>
            <DialogDescription>
              {respondTo ? `${COVER_LABELS[respondTo.cover_type]} · ${money(respondTo.insured_value, respondTo.currency)} sum insured` : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendQuote} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Premium</Label>
                <Input data-testid="agent-quote-premium-input" type="number" step="0.01" min="0.01" required value={rForm.premium} onChange={(e) => setRForm({ ...rForm, premium: e.target.value })} />
              </div>
              <div>
                <Label>Excess</Label>
                <Input data-testid="agent-quote-excess-input" type="number" step="0.01" min="0" value={rForm.excess} onChange={(e) => setRForm({ ...rForm, excess: e.target.value })} />
              </div>
              <div>
                <Label>Term (days)</Label>
                <Input data-testid="agent-quote-term-input" type="number" min="1" value={rForm.term_days} onChange={(e) => setRForm({ ...rForm, term_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Conditions</Label>
              <Textarea data-testid="agent-quote-note-input" rows={2} value={rForm.note} onChange={(e) => setRForm({ ...rForm, note: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-agent-quote-button" className="w-full sm:w-auto">Send quote</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Patch dialog ---------------- */}
      <Dialog open={areaOpen} onOpenChange={setAreaOpen}>
        <DialogContent className="sm:max-w-md" data-testid="area-dialog">
          <DialogHeader>
            <DialogTitle>My patch</DialogTitle>
            <DialogDescription>Only risks inside your patch appear on your desk.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveArea} className="space-y-3">
            <div>
              <Label>Country</Label>
              <Select value={area.country} onValueChange={(v) => setArea({ ...area, country: v })}>
                <SelectTrigger data-testid="area-country-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City / region</Label>
              <Input data-testid="area-city-input" value={area.city} onChange={(e) => setArea({ ...area, city: e.target.value })} />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                data-testid="area-whole-country-checkbox"
                checked={area.covers_whole_country}
                onChange={(e) => setArea({ ...area, covers_whole_country: e.target.checked })}
                className="h-4 w-4 accent-[#002FA7]"
              />
              Cover the whole country, not just my city
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                data-testid="area-international-checkbox"
                checked={area.international}
                onChange={(e) => setArea({ ...area, international: e.target.checked })}
                className="h-4 w-4 accent-[#002FA7]"
              />
              Also write international / cross-border risks
            </label>
            <DialogFooter>
              <Button type="submit" data-testid="submit-area-button" className="w-full sm:w-auto">Save patch</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
